import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, ExternalLink, Monitor } from 'lucide-react';
import { EXTERNAL_LINK_PROPS, getSidebarApps } from '../lib/appLinks';
import { embedPolicyForApp, getEmbedConfigByAppId } from '../lib/embedConfig';

interface ExternalAppsNavProps {
  expanded: boolean;
}

function ExternalAppsNav({ expanded }: ExternalAppsNavProps) {
  const [open, setOpen] = useState(true);
  const apps = getSidebarApps();

  if (!expanded) {
    return (
      <div className="mt-3 border-t border-zt-border pt-2.5">
        <div className="flex flex-col gap-1">
          {apps.map((app) => (
            <a
              key={app.id}
              href={app.url}
              {...EXTERNAL_LINK_PROPS}
              className="flex min-h-9 items-center justify-center rounded-md p-1.5 text-zt-muted transition-colors hover:bg-zt-elevated hover:text-zt-primary"
              title={app.name}
            >
              <app.icon size={17} />
            </a>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-zt-border pt-2.5">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="mb-1.5 flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-zt-muted hover:bg-zt-elevated hover:text-slate-200"
        aria-expanded={open}
      >
        <span>External apps</span>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>

      {open && (
        <div className="flex flex-col gap-1">
          {apps.map((app) => {
            const embed = getEmbedConfigByAppId(app.id);
            const canEmbed = embedPolicyForApp(app.id) === 'allowed' && embed;
            return (
              <div key={app.id} className="group flex items-center gap-0.5">
                <a
                  href={app.url}
                  {...EXTERNAL_LINK_PROPS}
                  className="flex min-h-9 min-w-0 flex-1 items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium leading-snug text-zt-muted transition-colors hover:bg-zt-elevated hover:text-slate-200"
                  title={app.description}
                >
                  <app.icon size={15} className="shrink-0 group-hover:text-zt-primary" />
                  <span className="min-w-0 flex-1 truncate">{app.name}</span>
                  <ExternalLink
                    size={11}
                    className="shrink-0 opacity-0 transition-opacity group-hover:opacity-60"
                  />
                </a>
                {canEmbed && (
                  <Link
                    to={embed.route}
                    className="rounded p-1.5 text-zt-muted opacity-0 transition-opacity hover:bg-zt-elevated hover:text-zt-accent group-hover:opacity-100"
                    title={`Embed ${app.name} in dashboard`}
                  >
                    <Monitor size={13} />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ExternalAppsNav;
